<template>
  <div
    v-loading="loading"
    class="width-100"
  >
    <el-page-header
      :title="$t('global.back')"
      :content="heading"
      @back="goToSamplesIndex"
    />
    <el-divider />
    <el-result
      v-if="errorMessage"
      icon="error"
      :title="$t('sampleProgress.error.title')"
      :sub-title="errorMessage"
    />
    <el-result
      v-else-if="hasNoProgress"
      icon="info"
      :title="$t('sampleProgress.empty.title')"
      :sub-title="$t('sampleProgress.empty.subtitle')"
    />
    <template v-else-if="steps">
      <el-card>
        <ProgressStepper
          :steps="steps"
          :selected-step-id="selectedStepId"
          @select="selectStep"
        />
      </el-card>
      <!-- One panel at a time: the files stay one click away however long the details get. -->
      <el-card>
        <el-tabs v-model="activeTab">
          <!-- Named after the step: the full label, which the stepper may cut short. -->
          <el-tab-pane
            :name="$options.TABS.DETAILS"
            :label="selectedStepLabel"
            :disabled="!hasDetails"
          >
            <el-alert
              v-if="domainMetaFailed"
              :title="$t('sampleProgress.domainMeta.error')"
              type="error"
              show-icon
              :closable="false"
            />
            <ProgressDomainMeta
              v-else-if="selectedDomainMeta"
              :loading="domainMetaLoading"
              :content-html="selectedDomainMeta.contentHtml"
            />
          </el-tab-pane>
          <!-- Of the selected step, like the details. Rendered, and requested, on first open. -->
          <el-tab-pane
            :name="$options.TABS.EVENTS"
            :label="$t('sampleProgress.tabs.events')"
            lazy
          >
            <ProgressTimeline
              :sample-id="sampleId"
              :step-id="selectedStepId"
            />
          </el-tab-pane>
          <!-- Of the selected step too: all of them are loaded once, selecting filters them. -->
          <el-tab-pane
            :name="$options.TABS.FILES"
            :label="filesLabel"
          >
            <el-alert
              v-if="filesFailed"
              :title="$t('sampleProgress.files.error')"
              type="error"
              show-icon
              :closable="false"
            />
            <ProgressFiles
              v-else-if="stepFiles"
              :sample-id="sampleId"
              :files="stepFiles"
              :steps="steps"
            />
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { samplesApi, sampleProgressApi } from '@/api';
import ProgressDomainMeta from '@/components/ProgressDomainMeta.vue';
import ProgressFiles from '@/components/ProgressFiles.vue';
import ProgressStepper from '@/components/ProgressStepper.vue';
import ProgressTimeline from '@/components/ProgressTimeline.vue';
import progressFile from '@/services/progressFile';
import progressStep from '@/services/progressStep';

const TABS = {
  DETAILS: 'details',
  EVENTS: 'events',
  FILES: 'files',
};

// The details are the default tab: only the others are written in the URL.
const URL_TABS = [TABS.EVENTS, TABS.FILES];

export default {
  name: 'SampleProgress',
  TABS,
  components: {
    ProgressDomainMeta,
    ProgressFiles,
    ProgressStepper,
    ProgressTimeline,
  },
  props: {
    sampleId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      sample: null,
      steps: null,
      selectedStepId: null,
      errorMessage: null,
      // One row per step at most: steps without domainMeta have none.
      domainMetaByStepId: {},
      domainMetaLoading: false,
      domainMetaFailed: false,
      files: null,
      filesFailed: false,
      // The tab the user chose, back to the details on another step. From the URL, so a reload
      // or a shared link opens on the same tab.
      chosenTab: URL_TABS.includes(this.$route.query.tab) ? this.$route.query.tab : TABS.DETAILS,
      // Read once: switching tab rewrites the route, and with it where the page was reached from.
      cameFromList: false,
    };
  },
  computed: {
    ...mapGetters([
      'currentRoute',
    ]),
    heading() {
      return this.sample ? `${this.sample.productName} - ${this.sample.id}` : '';
    },
    hasNoProgress() {
      return !!this.steps && !this.steps.length;
    },
    selectedDomainMeta() {
      return this.domainMetaByStepId[this.selectedStepId] || null;
    },
    // W3: no details section for a step without domainMeta. An error still has to be told.
    hasDetails() {
      return this.domainMetaFailed || !!this.selectedDomainMeta;
    },
    selectedStep() {
      return this.steps.find(({ stepId }) => stepId === this.selectedStepId) || null;
    },
    // Every sample with Progress has a current step, which opens selected: the files are never
    // loaded without a step to filter them by.
    stepFiles() {
      return this.files && this.selectedStep
        ? progressFile.ofStep(this.files, this.selectedStep)
        : null;
    },
    filesLabel() {
      return this.stepFiles
        ? this.$t('sampleProgress.tabs.filesCount', { n: this.stepFiles.length })
        : this.$t('sampleProgress.tabs.files');
    },
    selectedStepLabel() {
      return this.selectedStep ? progressStep.label(this.selectedStep) : '';
    },
    // The chosen tab whenever the selected step has it. The details of a step without any give
    // way to the files.
    activeTab: {
      get() {
        return this.chosenTab === TABS.DETAILS && !this.hasDetails ? TABS.FILES : this.chosenTab;
      },
      set(tab) {
        this.chosenTab = tab;
      },
    },
  },
  watch: {
    // The choice, not the fallback: a link keeps opening on the details where a step has them.
    chosenTab(tab) {
      const { tab: _, ...query } = this.$route.query;
      this.$router.replace({ query: URL_TABS.includes(tab) ? { ...query, tab } : query });
    },
    // The language switch does not reload the page: the snapshot and the labels follow it.
    '$i18n.locale': function reloadTranslated() {
      if (!this.steps?.length) return;
      this.loadDomainMeta();
      this.loadStepLabels();
    },
    // A new id in the URL (or Back/Forward) reuses this instance: created does not rerun
    sampleId: 'load',
  },
  created() {
    this.cameFromList = this.currentRoute.from.name === 'samplesIndex';
    this.load();
  },
  methods: {
    async load() {
      const request = {};
      this.latestRequest = request;
      this.sample = null;
      this.steps = null;
      this.errorMessage = null;
      // The previous sample's snapshot and files, and any answer still on its way for them, are
      // dropped
      this.latestDomainMetaRequest = null;
      this.latestStepLabelsRequest = null;
      this.domainMetaByStepId = {};
      this.domainMetaLoading = false;
      this.domainMetaFailed = false;
      this.latestFilesRequest = null;
      this.files = null;
      this.filesFailed = false;
      if (!Number.isSafeInteger(this.sampleId) || this.sampleId <= 0) {
        this.loading = false;
        this.errorMessage = this.$t('sampleProgress.error.invalidSample');
        return;
      }

      this.loading = true;
      // A stale answer returns early: it belongs to a sample the page no longer shows
      try {
        const [sampleResponse, stepsResponse] = await Promise.all([
          samplesApi.findById(this.sampleId),
          sampleProgressApi.findSteps(this.sampleId),
        ]);
        if (this.latestRequest !== request) return;
        this.sample = sampleResponse.data[0] || null;
        this.steps = stepsResponse.data;
        this.selectedStepId = this.steps.find(step => step.isCurrent)?.stepId ?? null;
        if (this.steps.length) {
          await Promise.all([this.loadDomainMeta(), this.loadFiles()]);
          if (this.latestRequest !== request) return;
        }
      } catch (error) {
        if (this.latestRequest !== request) return;
        this.errorMessage = error.response?.data?.error?.message
          || this.$t('sampleProgress.error.subtitle');
      }
      this.loading = false;
    },
    // Another step opens on its details, or its files when it has none (see activeTab): on the
    // tab left open, the switch would go unnoticed.
    selectStep(stepId) {
      if (stepId === this.selectedStepId) return;
      this.selectedStepId = stepId;
      this.chosenTab = TABS.DETAILS;
    },
    // Only the labels: the steps, the selection and the current one stay as loaded.
    async loadStepLabels() {
      const request = {};
      this.latestStepLabelsRequest = request;
      try {
        const { data } = await sampleProgressApi.findSteps(this.sampleId);
        if (this.latestStepLabelsRequest !== request) return;
        const labels = Object.fromEntries(data.map(step => [step.stepId, step.label]));
        this.steps = this.steps.map(step => ({
          ...step,
          label: labels[step.stepId] ?? step.label,
        }));
      } catch (error) {
        // The labels of the previous language stay: nothing else depends on them.
      }
    },
    // Every step at once, so selecting another step needs no request.
    async loadDomainMeta() {
      const request = {};
      this.latestDomainMetaRequest = request;
      this.domainMetaLoading = true;
      try {
        const { data } = await sampleProgressApi.findDomainMeta(
          this.sampleId,
          { lang: this.$i18n.locale },
        );
        // A newer request, for the language switched to or the sample opened meanwhile, is the
        // one that counts.
        if (this.latestDomainMetaRequest !== request) return;
        this.domainMetaByStepId = Object.fromEntries(data.map(row => [row.stepId, row]));
        this.domainMetaFailed = false;
      } catch (error) {
        if (this.latestDomainMetaRequest !== request) return;
        // Not the previous language's snapshot under an error.
        this.domainMetaByStepId = {};
        this.domainMetaFailed = true;
      } finally {
        if (this.latestDomainMetaRequest === request) this.domainMetaLoading = false;
      }
    },
    // Not localized: no need to follow the language switch.
    async loadFiles() {
      const request = {};
      this.latestFilesRequest = request;
      try {
        const { data } = await sampleProgressApi.findFiles(this.sampleId);
        // The answer for a sample left meanwhile.
        if (this.latestFilesRequest !== request) return;
        this.files = data;
      } catch (error) {
        if (this.latestFilesRequest !== request) return;
        this.filesFailed = true;
      }
    },
    // Back to the list as it was left, filters and page included: they live in its URL. Reached
    // any other way (a link, a reload) the list opens on the filters kept in the store.
    goToSamplesIndex() {
      if (this.cameFromList) {
        this.$router.back();
      } else {
        this.$router.push({ name: 'samplesIndex' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.el-card + .el-card {
  margin-top: $--card-padding;
}
</style>
